<script lang="ts">
  import { Activity, ImageType, type Exercise } from '@/Common/ActivityFunctions';
  import { calculateCompletedXSS, convertSeconds, formatXSSDetails, kmToMile, tidy, xssDeviationToCategory, type XSSDetails, type XSSDeviationDetails } from '@/Util/utils';
  import { format } from 'date-fns';
  import { ActivityTag } from '@/Common/ActivityTag';
  import { TagWrapper } from '@/Common';
  import GroupMemberAvatars from '@/Common/GroupMemberAvatars.svelte';
  import TodayActivityCardDuplicatesSection from './TodayActivityCardDuplicatesSection.svelte';
  import XSSDisplay from '../../Common/XSSDisplay.svelte';
  import TodayActivityCardActions from './TodayActivityCardActions.svelte';
  import { cn } from '@/lib/utils';
  import { Separator } from '@/lib/components/ui/separator';
  import { Button } from '@/lib/components/ui/button';
  import type { TodaysActivities } from '@/Common/constants';
  import { _ } from 'svelte-i18n';

  export let activity: Exercise;
  export let todaysActivities: TodaysActivities;
  export let selectable: boolean = false;
  export let onSelectedChange: (selected: boolean) => void = () => {};
  // This slightly changes how the title, activity time, xss, distance, and duration are laid out
  // to put the activity time in the top right of the card (better suited for timeline view)
  export let useTimelineLayout: boolean = false;
  export let activityImageCarouselNumber: Record<string, number>;

  export let selected: boolean = false;
  export let hideActions: boolean = false;

  $: xssDetails = {
    xss: activity.xss ?? 0,
    xlss: activity.xlss ?? 0,
    xhss: activity.xhss ?? 0,
    xpss: activity.xpss ?? 0,
  };
  $: formattedXSSDetails = formatXSSDetails(xssDetails);

  $: matchedActivities = todaysActivities.activities.filter(a => Activity.isMatchedAtoB(a, activity));
  
  $: completedXSS = calculateCompletedXSS(activity, matchedActivities);

  let xssDeviationCategory: XSSDetails<XSSDeviationDetails> | null = null;
  $: if (matchedActivities && matchedActivities.length > 0 && xssDetails && completedXSS) {
    xssDeviationCategory = xssDeviationToCategory(xssDetails, completedXSS);
  }

  let isLoading = false;

  $: isHidden = Activity.isHidden(activity);
  $: isDuplicate = Activity.isDuplicate(activity);
  $: isPlanned = Activity.isPlanned(activity);
  $: isForecast = Activity.isForecast(activity);
  $: isPlaceholder = Activity.isPlaceholder(activity);

  $: images = Activity.getAllImages(activity);

  //@ts-ignore
  const Xert = window.Xert;
  let useFreedomUnits: boolean = Xert._displayUnits == 'imperial';

  const activityTag = ActivityTag('small');
  $: activityTags = [
    activityTag.hidden(activity),
    !isForecast ? activityTag.planned(activity) : null,
    isPlanned && !isPlaceholder ? activityTag.exerciseType(activity) : null, 
    activityTag.completed({ ...activity, scheduled: activity.scheduled, activity_mode: activity.activity_mode }),
    activityTag.device(activity),
    activityTag.strava(activity),
    activityTag.powerSource(activity),
    activityTag.merged(activity),
    activityTag.breakthrough(activity),
    activityTag.group(activity),
  ].filter((tag) => tag);

  let containerSize: ResizeObserverSize[] | null = null;
  let containerWidth: number | null = null;
  $: if (containerSize && containerSize[0]) containerWidth = containerSize[0].inlineSize;

  let textContainerSize: ResizeObserverSize[] | null = null;
  let textContainerHeight: number | null = null;
  $: if (textContainerSize && textContainerSize[0]) textContainerHeight = textContainerSize[0].blockSize;

  let hideImage: boolean = false;
  $: if (activity) hideImage = false;

</script>

<!-- svelte-ignore a11y-click-events-have-key-events -->
<!-- svelte-ignore a11y-no-static-element-interactions -->
<div class="flex flex-row gap-2 w-full" bind:borderBoxSize={containerSize}>
  <div class='flex flex-col flex-1 gap-2 h-max' bind:borderBoxSize={textContainerSize}>
    <div class="flex flex-wrap items-center justify-between gap-2">
      <div class="flex flex-wrap items-center gap-3">
        {#if selectable}
          <Button variant={selected ? 'default' : 'outline'} size='sm' class='p-0 w-5 h-5 flex items-center justify-center' on:click={(e) => { e.stopPropagation(); selected = !selected; onSelectedChange(selected); }}>
            {#if selected}
              <i class='fa-solid fa-check text-sm' />
            {/if}
          </Button>
        {/if}
        {#if isLoading || activityTags.length > 0}
          <div class='flex flex-row items-center gap-1'>
            {#if isLoading}
              <i class="fa-solid fa-spinner fa-spin text-gray-400" />
            {/if}
            {#if activityTags.length > 0}
              <TagWrapper tags={activityTags} groupMembers={activity.group_members ?? []} />
            {/if}
          </div>
        {/if}
        <h3 class="text-base font-medium m-0">
          {#if Activity.getDetailsPageLink(activity)}
            <a class={cn('hover:underline', (isHidden || isDuplicate) ? 'text-muted-foreground' : 'text-foreground')} href={Activity.getDetailsPageLink(activity)} target={Activity.isActivity(activity) ? "activity" : "workout"}>
              {activity.name}
            </a>
          {:else}
            {activity.name}
          {/if}
        </h3>
      </div>
      {#if useTimelineLayout}
        <div class="text-sm text-gray-500">{format(new Date(activity.start_date), 'hh:mm aa')}</div>
      {:else}
        <div class="flex flex-wrap items-center gap-3">
          <span class="text-sm text-gray-500">{format(new Date(activity.start_date), 'hh:mm aa')}</span>
          <span class="text-sm text-gray-500">{tidy(useFreedomUnits ? kmToMile(activity.distance ?? 0) : (activity.distance ?? 0))} {useFreedomUnits ? 'mi' : 'km'}</span>
          <span class="text-sm text-gray-500">{convertSeconds(activity.duration ?? activity.adu)}</span>
          <span>
            XSS:
            <XSSDisplay
              xss={formattedXSSDetails}
              grayscale={isHidden || isDuplicate}
            />
          </span>
        </div>
      {/if}
    </div>
    {#if useTimelineLayout}
      <div class="mt-1 flex flex-wrap items-center gap-3 text-sm text-gray-600">
        <span class="text-sm text-gray-500">{tidy(useFreedomUnits ? kmToMile(activity.distance ?? 0) : (activity.distance ?? 0))} {useFreedomUnits ? 'mi' : 'km'}</span>
        <span class="text-sm text-gray-500">{convertSeconds(activity.duration ?? activity.adu)}</span>
        <span>
          XSS:
          <XSSDisplay
            xss={formattedXSSDetails}
            grayscale={isHidden || isDuplicate}
          />
        </span>
      </div>
    {/if}

    {#if containerWidth < 600}
      {#if images.length > 0 && !hideImage}
        {@const image = images[activityImageCarouselNumber[activity.path] ?? 0]}
        <div class={cn('w-full rounded-md border border-border',
            containerWidth >= 400 ? (containerWidth >= 500 ? 'h-56' : 'h-48') : 'h-36',
            images.length > 1 ? 'cursor-pointer transition hover:brightness-90' : '',
          )}
          on:click={(e) => {
            if (images.length > 1) {
              e.stopPropagation();
              activityImageCarouselNumber[activity.path] = ((activityImageCarouselNumber[activity.path] ?? 0) + 1) % images.length;
            }
          }}
        >
          <img class={cn('w-full h-full',
              image?.type === ImageType.Map || image?.type === ImageType.StreetView ? 'object-cover' : image?.type === ImageType.MagicBuckets ? 'object-contain p-1' : 'object-fill',
              isHidden ? 'grayscale' : '',
            )}
            src={image?.url}
            alt={image?.type} draggable="false"
            on:error={() => { hideImage = true; }}
          />
        </div>
      {/if}
    {/if}
  
    {#if activity.group_members?.length}
      <div class="flex items-center gap-2 text-sm text-gray-600">
        <i class="fa-solid fa-users text-gray-500"></i>
        <GroupMemberAvatars members={activity.group_members} />
      </div>
    {/if}

    {#if !hideActions}
      <TodayActivityCardActions {todaysActivities} {activity} bind:disabled={isLoading} transparent={isHidden || isDuplicate} />
    {/if}
  
    {#if activity.is_currently_selected_duplicate}
      <TodayActivityCardDuplicatesSection {todaysActivities} bind:isLoading {activity} />
    {/if}
  </div>

  {#if containerWidth >= 600}
    {#if images.length > 0 && !hideImage}
      {@const image = images[activityImageCarouselNumber[activity.path] ?? 0]}
      <Separator orientation="vertical" />
      <div class={cn('rounded-md border border-border',
          containerWidth >= 700 ? 'w-64' : 'w-48',
          images.length > 1 ? 'cursor-pointer transition hover:brightness-90' : '',
        )}
        style={`height: ${Math.min(Math.max(textContainerHeight, 90), 150)}px;`}
        on:click={(e) => {
          if (images.length > 1) {
            e.stopPropagation();
            activityImageCarouselNumber[activity.path] = ((activityImageCarouselNumber[activity.path] ?? 0) + 1) % images.length;
          }
        }}
      >
        <img class={cn('w-full h-full',
            image?.type === ImageType.Map || image?.type === ImageType.StreetView ? 'object-cover' : image?.type === ImageType.MagicBuckets ? 'object-contain p-1' : 'object-fill',
            isHidden ? 'grayscale' : '',
          )}
          src={image?.url}
          alt={image?.type} draggable="false"
          on:error={() => { hideImage = true; }}
        />
      </div>
    {/if}
  {/if}
</div>
